import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { describeRank, formatPct, guidanceText } from "@/lib/benchmarks";

const POSITION_STYLES = {
  above_q3: {
    badge: "Top quartile",
    badgeClass: "bg-[rgba(27,125,88,0.12)] text-success-700",
    tone: "text-success-700",
    headline: "Leading",
  },
  median_to_q3: {
    badge: "Above median",
    badgeClass: "bg-[rgba(31,76,84,0.12)] text-[var(--accent)]",
    tone: "text-[var(--accent)]",
    headline: "On track",
  },
  at_median: {
    badge: "At median",
    badgeClass: "bg-[rgba(31,76,84,0.12)] text-[var(--accent)]",
    tone: "text-[var(--accent)]",
    headline: "On track",
  },
  q1_to_median: {
    badge: "Below median",
    badgeClass: "bg-[rgba(153,98,15,0.15)] text-warning-900",
    tone: "text-warning-700",
    headline: "Developing",
  },
  below_q1: {
    badge: "Bottom quartile",
    badgeClass: "bg-[rgba(125,35,35,0.15)] text-danger-700",
    tone: "text-danger-700",
    headline: "Immediate focus",
  },
};

const clampPosition = (value) => Math.min(100, Math.max(0, value));

const wholePct = (value) => `${Math.round(value)}%`;

// Where a score sits among a peer group: exact rank, quartile band and median
export default function PercentileBand({ label, groupLabel, comparison }) {
  const styles = POSITION_STYLES[comparison.position];
  const rankInfo = describeRank(comparison.rank, groupLabel);
  const shareText = comparison.rank.equal > 0
    ? `${wholePct(comparison.lower_pct)} scored lower and ${wholePct(comparison.same_pct)} scored the same`
    : `${wholePct(comparison.lower_pct)} scored lower`;
  const rankSentence = rankInfo.extreme
    ? `${rankInfo.text}.`
    : `${rankInfo.text} among ${groupLabel} peers: ${shareText}.`;

  const score = comparison.user_pct;
  const q1 = clampPosition(comparison.q1_pct);
  const median = clampPosition(comparison.median_pct);
  const q3 = clampPosition(comparison.q3_pct);
  const scorePosition = clampPosition(score);

  const deltaToMedian = comparison.user_pct - comparison.median_pct;
  const deltaLabel = comparison.position === "at_median"
    ? "At the median"
    : `${deltaToMedian >= 0 ? "+" : ""}${deltaToMedian.toFixed(1)} pts vs median`;

  // Check for label collisions
  const tooCloseToScale = [0, 25, 50, 75, 100].some(
    mark => Math.abs(scorePosition - mark) < 4
  );
  const tooCloseToMedian = Math.abs(scorePosition - median) < 8;

  return (
    <section className="space-y-4 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/90 p-8 shadow-[0_28px_56px_rgba(15,23,42,0.08)]">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <p className="eyebrow-muted">
            {label}
          </p>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)]">
              {Math.round(score)}%
            </span>
            <span className="text-sm text-[var(--text-secondary)]">({rankInfo.text})</span>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 text-left sm:items-end">
          {styles?.badge && (
            <span className={cn("rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider", styles.badgeClass)}>
              {styles.badge}
            </span>
          )}
          <span className="text-xs font-medium text-[var(--text-secondary)]">
            {deltaLabel}
          </span>
        </div>
      </header>

      {/* pt-8 keeps the median label (drawn above the bar) clear of the header on phones */}
      <div className="space-y-3 pt-8">
        <div className="relative h-4 w-full rounded-full bg-[rgba(31,76,84,0.05)] pt-10 pb-16">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, rgba(125,35,35,0.12) 0%, rgba(125,35,35,0.12) 25%, rgba(153,98,15,0.12) 25%, rgba(153,98,15,0.12) 50%, rgba(31,76,84,0.1) 50%, rgba(31,76,84,0.1) 75%, rgba(27,125,88,0.12) 75%, rgba(27,125,88,0.12) 100%)",
            }}
          />

          {/* The interquartile range; when all three quartiles coincide the median marker alone shows them */}
          <motion.div
            className="absolute top-0 h-full rounded-full bg-[rgba(31,76,84,0.18)]"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: `${q3 - q1}%`, left: `${q1}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />

          <div
            className="absolute flex flex-col items-center gap-1 whitespace-nowrap"
            style={{
              top: -32,
              left: `${median}%`,
              transform: 'translateX(-50%)'
            }}
            title={`Median: ${formatPct(median)}`}
          >
            <span className="text-xs font-semibold text-[var(--text-primary)]">
              Median {formatPct(median)}
            </span>
            <div className="h-10 w-[3px] rounded-full bg-[var(--text-primary)] shadow-sm" />
          </div>

          <div
            className="absolute flex flex-col items-center gap-1 whitespace-nowrap"
            style={{
              bottom: -56,
              left: `${scorePosition}%`,
              transform: 'translateX(-50%)'
            }}
            title={`Your score: ${formatPct(scorePosition)}`}
          >
            <div className="h-12 w-1 rounded-full bg-gradient-to-b from-[var(--accent)] to-[var(--accent)]/60 shadow-[0_4px_12px_rgba(31,76,84,0.4)]" />
            <div className="rounded-md bg-[var(--accent)] px-2.5 py-1 shadow-lg">
              <span className="text-[11px] font-bold uppercase tracking-wider text-white">
                {tooCloseToScale || tooCloseToMedian
                  ? `YOU · ${Math.round(score)}%`
                  : 'YOU'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-between text-xs font-medium text-[var(--text-soft)]">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </div>
      </div>

      <div className="space-y-2">
        {styles?.headline && (
          <p className={cn("text-sm font-semibold", styles.tone)}>
            {styles.headline}
          </p>
        )}
        <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
          {rankSentence} {guidanceText(comparison)}
        </p>
      </div>
    </section>
  );
}
